import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  getStoredMessages,
  getStoredSessionId,
  isChatMarkedFinished,
  postWebChat,
  setChatFinished,
  setStoredMessages,
  setStoredSessionId,
} from "../services/chatAiApi";

const WELCOME =
  "Сайн байна уу? Hello?";
const WELCOME_MESSAGE = { id: "welcome", role: "assistant", text: WELCOME, links: [] };

function stripLinksFromText(text, links) {
  if (typeof text !== "string" || !Array.isArray(links) || links.length === 0) {
    return text;
  }

  const escapedLinks = links
    .filter((url) => typeof url === "string" && url.trim().length > 0)
    .map((url) => url.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

  if (escapedLinks.length === 0) {
    return text;
  }

  const linkRegex = new RegExp(`\\b(?:${escapedLinks.join("|")})\\b`, "g");
  return text
    .split(/\r?\n/)
    .map((line) => line.replace(linkRegex, "").trimEnd())
    .filter((line, idx, arr) => {
      if (line.length > 0) {
        return true;
      }
      return idx > 0 && arr[idx - 1].length > 0;
    })
    .join("\n")
    .trim();
}

function normalizeLinks(links) {
  if (!Array.isArray(links)) {
    return [];
  }
  return Array.from(
    new Set(
      links.filter((url) => typeof url === "string" && url.trim().length > 0)
    )
  );
}

function parseStoreLink(url) {
  if (typeof url !== "string") {
    return null;
  }

  if (url.includes("play.google.com/store/apps/details?id=gobicraft.tomko")) {
    return { app: "Tomko (Language Learning App)", store: "Google Play Store", url };
  }

  if (
    url.includes("apps.apple.com") &&
    (url.includes("/tomko/id6749509851") || url.includes("/id6749509851"))
  ) {
    return { app: "Tomko (Language Learning App)", store: "Apple App Store", url };
  }

  if (
    url.includes("play.google.com/store/apps/details?id=gobicraft.tomkobooks")
  ) {
    return { app: "Tomko Books (Digital Reading App)", store: "Google Play Store", url };
  }

  if (
    url.includes("apps.apple.com") &&
    (url.includes("/tomko-books/id6749153308") || url.includes("/id6749153308"))
  ) {
    return { app: "Tomko Books (Digital Reading App)", store: "Apple App Store", url };
  }

  return null;
}

function renderAssistantLinks(messageId, links) {
  const parsed = links.map(parseStoreLink);
  const allKnown = parsed.every((item) => item !== null);

  if (!allKnown) {
    return (
      <div className="chat-ai-sidebar__links">
        {links.map((url) => (
          <a
            key={`${messageId}-${url}`}
            href={url}
            className="chat-ai-sidebar__link"
            target="_blank"
            rel="noreferrer"
          >
            {url}
          </a>
        ))}
      </div>
    );
  }

  const grouped = parsed.reduce((acc, item) => {
    const key = item.app;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <div className="chat-ai-sidebar__bubble-text">
      {Object.entries(grouped).map(([app, appLinks]) => (
        <div key={`${messageId}-${app}`}>
          <p>{app}</p>
          {appLinks.map((entry) => (
            <React.Fragment key={`${messageId}-${entry.url}`}>
              <p>{entry.store}:</p>
              <a
                href={entry.url}
                className="chat-ai-sidebar__link"
                target="_blank"
                rel="noreferrer"
              >
                {entry.url}
              </a>
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}

export function ChatAiSidebar() {
  const finished = isChatMarkedFinished();
  const storedMessages = getStoredMessages();
  const storedSessionId = getStoredSessionId();
  const hasUnfinishedChat =
    !finished && (storedMessages.length > 0 || Boolean(storedSessionId));

  const [open, setOpen] = useState(() => hasUnfinishedChat);
  const [messages, setMessages] = useState(() => {
    if (finished) {
      return [WELCOME_MESSAGE];
    }
    return storedMessages.length > 0 ? storedMessages : [WELCOME_MESSAGE];
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(() => (finished ? "" : storedSessionId));
  const listEndRef = useRef(null);
  const inputRef = useRef(null);

  const configured = Boolean(
    process.env.REACT_APP_CHAT_AI_API_URL &&
      process.env.REACT_APP_CHAT_AI_API_TOKEN
  );

  useEffect(() => {
    if (open) {
      listEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, loading]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    const nonWelcomeMessages = messages.filter((m) => m.id !== "welcome");
    setStoredMessages(nonWelcomeMessages);
  }, [messages]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading || !configured) {
      return;
    }
    setError(null);
    setInput("");
    const userMsg = { id: `u-${Date.now()}`, role: "user", text };
    setMessages((m) => [...m, userMsg]);
    setLoading(true);
    try {
      const { reply, links, sessionId: nextSid } = await postWebChat({
        message: text,
        sessionId: sessionId || undefined,
      });
      setSessionId(nextSid);
      setStoredSessionId(nextSid);
      setChatFinished(false);
      setMessages((m) => [
        ...m,
        { id: `a-${Date.now()}`, role: "assistant", text: reply, links },
      ]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Something went wrong.";
      setError(msg);
      setMessages((m) => m.filter((x) => x.id !== userMsg.id));
      setInput(text);
    } finally {
      setLoading(false);
    }
  }, [configured, input, loading, sessionId]);

  const newChat = useCallback(() => {
    setChatFinished(false);
    setSessionId("");
    setStoredSessionId("");
    setMessages([WELCOME_MESSAGE]);
    setError(null);
    setInput("");
  }, []);

  const finishChat = useCallback(() => {
    setChatFinished(true);
    setSessionId("");
    setMessages([WELCOME_MESSAGE]);
    setError(null);
    setInput("");
    setOpen(false);
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  if (!configured) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="chat-ai-sidebar__fab"
        aria-expanded={open}
        aria-controls="chat-ai-sidebar-panel"
        aria-label={open ? "Close assistant" : "Open assistant"}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="chat-ai-sidebar__fab-icon" aria-hidden="true">
          {open ? (
            "×"
          ) : (
            <i className="fa fa-comments" aria-hidden="true" />
          )}
        </span>
        {!open && (
          <span className="chat-ai-sidebar__fab-label">Assistant</span>
        )}
      </button>

      {open && (
        <div
          className="chat-ai-sidebar__backdrop"
          aria-hidden="true"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        id="chat-ai-sidebar-panel"
        className={`chat-ai-sidebar${open ? " chat-ai-sidebar--open" : ""}`}
        aria-hidden={!open}
      >
        <header className="chat-ai-sidebar__header">
          <div>
            <h2 className="chat-ai-sidebar__title">AI Agent</h2>
            <p className="chat-ai-sidebar__subtitle">Gobicraft chat agent v1</p>
          </div>
          <div className="chat-ai-sidebar__header-actions">
            <button
              type="button"
              className="chat-ai-sidebar__ghost-btn"
              onClick={newChat}
              disabled={loading}
            >
              New chat
            </button>
            <button
              type="button"
              className="chat-ai-sidebar__icon-btn"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>
        </header>

        <div className="chat-ai-sidebar__messages" role="log" aria-live="polite">
          {messages.map((m) => {
            const links = normalizeLinks(m.links);
            const cleanedText =
              m.role === "assistant" ? stripLinksFromText(m.text, links) : m.text;

            return (
              <div
                key={m.id}
                className={`chat-ai-sidebar__bubble chat-ai-sidebar__bubble--${m.role}`}
              >
                {cleanedText && (
                  <p className="chat-ai-sidebar__bubble-text">{cleanedText}</p>
                )}
                {m.role === "assistant" && links.length > 0 && (
                  renderAssistantLinks(m.id, links)
                )}
              </div>
            );
          })}
          {loading && (
            <div className="chat-ai-sidebar__bubble chat-ai-sidebar__bubble--assistant chat-ai-sidebar__typing">
              <span />
              <span />
              <span />
            </div>
          )}
          <div ref={listEndRef} />
        </div>

        {error && (
          <div className="chat-ai-sidebar__banner chat-ai-sidebar__banner--err">
            {error}
          </div>
        )}

        <form className="chat-ai-sidebar__composer" onSubmit={onSubmit}>
          <textarea
            ref={inputRef}
            className="chat-ai-sidebar__input"
            rows={2}
            placeholder="Message…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={!configured}
            maxLength={8000}
          />
          <button
            type="submit"
            className="chat-ai-sidebar__send"
            disabled={loading || !input.trim() || !configured}
          >
            Send
          </button>
        </form>

        <button
          type="button"
          className="chat-ai-sidebar__finish-btn"
          onClick={finishChat}
          disabled={loading}
        >
          Finish chat
        </button>

        <footer className="chat-ai-sidebar__footer">
          <Link to="/chat-ai-privacy">Privacy</Link>
          <span aria-hidden="true"> · </span>
          <Link to="/chat-ai-terms">Terms</Link>
        </footer>
      </aside>
    </>
  );
}
